import {
  useMutation,
  useQueryClient,
  UseMutationResult,
  UseMutationOptions,
} from "@tanstack/react-query"
import { guardAppUsage } from "@mimber-client/guards/AppGuard"
import { useServerContext } from "@mimber-client/contexts/ServerContext"
import {
  VoidInvoiceDto,
  InvoiceWithRelationsDto,
  ResponseDto,
} from "@mimber/schema"
import { invoicesKeys } from "../invoices.utils"

interface VoidInvoiceOptions extends Omit<
  UseMutationOptions<
    ResponseDto<InvoiceWithRelationsDto>,
    Error,
    VoidInvoiceDto
  >,
  "mutationFn"
> {}

export const useVoidInvoice = (
  uuid: string,
  options: VoidInvoiceOptions = {}
): UseMutationResult<
  ResponseDto<InvoiceWithRelationsDto>,
  Error,
  VoidInvoiceDto
> => {
  guardAppUsage("mimber-organization", "useVoidInvoice")

  const { server } = useServerContext()
  const queryClient = useQueryClient()

  return useMutation({
    ...options,
    mutationFn: async (data: VoidInvoiceDto) => {
      const response = await server.post<ResponseDto<InvoiceWithRelationsDto>>(
        `invoices/${uuid}/void`,
        data
      )
      return response.data
    },
    onSuccess: (response, variables, onMutateResult, context) => {
      queryClient.invalidateQueries({ queryKey: invoicesKeys.findOne(uuid) })
      queryClient.invalidateQueries({ queryKey: invoicesKeys.findAll() })
      options?.onSuccess?.(response, variables, onMutateResult, context)
    },
  })
}
